import { useVirtualizer } from '@tanstack/vue-virtual'
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

import { filterFileBoxes, getInitialActiveIndex, getWrappedIndex } from './fileBoxSelectorUtils'

const LIST_ID = 'file-box-selector-list'
const PLACEHOLDER = '– Choose a Folder –'
const ROW_HEIGHT = 32
const VIEWPORT_HEIGHT = 240

export default {
  name: 'FileBoxSelector',

  props: {
    fileBoxes: {
      required: true,
      type: Array,
    },
  },

  setup(props) {
    const root = ref(null)
    const trigger = ref(null)
    const search = ref(null)
    const list = ref(null)
    const isOpen = ref(false)
    const searchQuery = ref('')
    const highlightedIndex = ref(getInitialActiveIndex(props.fileBoxes))

    const filteredFileBoxes = computed(() => filterFileBoxes(props.fileBoxes, searchQuery.value))
    const activeFileBox = computed(() => props.fileBoxes.find((fileBox) => fileBox.active))
    const activeDescendantId = computed(() => {
      const fileBox = filteredFileBoxes.value[highlightedIndex.value]

      return fileBox ? `file-box-selector-option-${fileBox.id}` : null
    })
    const virtualizer = useVirtualizer(
      computed(() => ({
        count: filteredFileBoxes.value.length,
        estimateSize: () => ROW_HEIGHT,
        getScrollElement: () => list.value,
        overscan: 5,
      })),
    )
    const virtualRows = computed(() => virtualizer.value.getVirtualItems())
    const totalSize = computed(() => virtualizer.value.getTotalSize())

    function focusTrigger() {
      nextTick(() => trigger.value?.focus())
    }

    function close() {
      isOpen.value = false
      searchQuery.value = ''
      highlightedIndex.value = getInitialActiveIndex(props.fileBoxes)
      focusTrigger()
    }

    function open() {
      if (isOpen.value) {
        return
      }

      isOpen.value = true
      searchQuery.value = ''
      highlightedIndex.value = getInitialActiveIndex(props.fileBoxes)

      nextTick(() => {
        search.value?.focus()
        scrollToHighlightedIndex()
      })
    }

    function toggle() {
      if (isOpen.value) {
        close()
      } else {
        open()
      }
    }

    function handleDocumentClick(event) {
      if (isOpen.value && !root.value.contains(event.target)) {
        close()
      }
    }

    function handleTriggerKeydown(event) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault()
        open()
      }
    }

    function setSearchQuery(event) {
      const query = event.target.value

      searchQuery.value = query
      const matches = filterFileBoxes(props.fileBoxes, query)
      highlightedIndex.value = matches.length ? 0 : -1
      scrollToHighlightedIndex()
    }

    function scrollToHighlightedIndex() {
      if (highlightedIndex.value !== -1) {
        nextTick(() => virtualizer.value.scrollToIndex(highlightedIndex.value))
      }
    }

    function moveHighlightedIndex(step) {
      highlightedIndex.value = getWrappedIndex(highlightedIndex.value, step, filteredFileBoxes.value.length)
      scrollToHighlightedIndex()
    }

    function handleSearchKeydown(event) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        moveHighlightedIndex(1)
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        moveHighlightedIndex(-1)
      } else if (event.key === 'Enter') {
        event.preventDefault()
        const fileBox = filteredFileBoxes.value[highlightedIndex.value]

        if (fileBox) {
          selectFileBox(fileBox)
        }
      } else if (event.key === 'Escape') {
        event.preventDefault()
        close()
      }
    }

    function selectFileBox(fileBox) {
      document.location.href = fileBox.url
    }

    function getOptionId(fileBox) {
      return `file-box-selector-option-${fileBox.id}`
    }

    function getRowStyle(virtualRow) {
      return {
        height: `${virtualRow.size}px`,
        top: `${virtualRow.start}px`,
      }
    }

    onMounted(() => document.addEventListener('click', handleDocumentClick))
    onBeforeUnmount(() => document.removeEventListener('click', handleDocumentClick))

    return {
      activeDescendantId,
      activeFileBox,
      close,
      filteredFileBoxes,
      getOptionId,
      getRowStyle,
      handleSearchKeydown,
      handleTriggerKeydown,
      highlightedIndex,
      isOpen,
      list,
      LIST_ID,
      open,
      PLACEHOLDER,
      root,
      search,
      searchQuery,
      selectFileBox,
      setSearchQuery,
      toggle,
      totalSize,
      trigger,
      virtualRows,
      VIEWPORT_HEIGHT,
    }
  },

  template: `
    <div ref="root" class="header-flex__box-picker">
      <div class="file-box-selector">
        <button
          ref="trigger"
          type="button"
          class="file-box-selector__trigger"
          aria-haspopup="listbox"
          :aria-expanded="isOpen"
          :aria-controls="LIST_ID"
          aria-label="Choose a folder"
          @click="toggle"
          @keydown="handleTriggerKeydown"
        >
          <span class="file-box-selector__label">{{ activeFileBox ? activeFileBox.name : PLACEHOLDER }}</span>
          <span class="file-box-selector__arrow" aria-hidden="true"></span>
        </button>

        <div v-if="isOpen" class="file-box-selector__menu">
          <label class="show-for-sr" for="file-box-selector-search">Search folders</label>
          <input
            id="file-box-selector-search"
            ref="search"
            class="file-box-selector__search"
            type="search"
            role="combobox"
            aria-autocomplete="list"
            aria-controls="file-box-selector-list"
            aria-expanded="true"
            :aria-activedescendant="activeDescendantId"
            placeholder="Search folders"
            :value="searchQuery"
            @input="setSearchQuery"
            @keydown="handleSearchKeydown"
          >

          <div
            v-if="filteredFileBoxes.length"
            :id="LIST_ID"
            ref="list"
            class="file-box-selector__list"
            role="listbox"
            aria-label="Folders"
            :style="{ height: VIEWPORT_HEIGHT + 'px' }"
          >
            <div class="file-box-selector__list-content" :style="{ height: totalSize + 'px' }">
              <button
                v-for="virtualRow in virtualRows"
                :id="getOptionId(filteredFileBoxes[virtualRow.index])"
                :key="filteredFileBoxes[virtualRow.index].id"
                type="button"
                role="option"
                tabindex="-1"
                class="file-box-selector__option"
                :class="{
                  'file-box-selector__option--active': filteredFileBoxes[virtualRow.index].active,
                  'file-box-selector__option--highlighted': virtualRow.index === highlightedIndex,
                }"
                :aria-selected="filteredFileBoxes[virtualRow.index].active"
                :title="filteredFileBoxes[virtualRow.index].name"
                :style="getRowStyle(virtualRow)"
                @mousedown.prevent
                @click="selectFileBox(filteredFileBoxes[virtualRow.index])"
              >
                {{ filteredFileBoxes[virtualRow.index].name }}
              </button>
            </div>
          </div>
          <div v-else class="file-box-selector__empty">No folders found</div>
        </div>
      </div>
    </div>
  `,
}
